import { AnimatePresence, motion } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useEffect, useRef, useState } from "react";
import {
    FaArrowDown,
    FaArrowUp,
    FaBitcoin,
    FaCubes,
    FaEthereum,
    FaFileInvoiceDollar,
    FaGlobe,
    FaPalette,
    FaShieldAlt,
    FaUniversity
} from "react-icons/fa";
import {
    TiArrowRight,
    TiArrowUp,
    TiChartArea,
    TiChartLineOutline,
    TiCompass,
    TiFlash,
    TiNews
} from "react-icons/ti";
import AnimatedTitle from "./AnimatedTitle";
import Footer from "./Footer";
import ScrollDownButton from './ScrollDownButton';
import GlassButton from './ui/GlassButton';
import { SparklesCore } from './ui/SparklesCore';

gsap.registerPlugin(ScrollTrigger);

const MarketData = () => {
  const [scrollDownClicked, setScrollDownClicked] = useState(false);
  const [isBottom, setIsBottom] = useState(false);
  const [activeChart, setActiveChart] = useState("Volume");

  const sectionRefs = useRef([]);
  const tickerRef = useRef(null);
  const ledgerRef = useRef(null);
  const scrollDownRef = useRef(null);
  const scrollTopRef = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    sectionRefs.current.forEach((section, index) => {
      if (section) {
        gsap.fromTo(
          section,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            scrollTrigger: {
              trigger: section,
              start: "top 90%",
            },
          }
        );
      }
    });

    if (tickerRef.current) {
      gsap.to(tickerRef.current, {
        x: "-50%",
        duration: 20,
        repeat: -1,
        ease: "none"
      });
    }

    if (scrollDownRef.current) {
      gsap.fromTo(
        scrollDownRef.current,
        { opacity: 0, y: -20 },
        { opacity: 1, y: 0, duration: 1 }
      );
    }
  }, []);

  const checkIfBottom = () => {
    if (window.innerHeight + window.scrollY >= document.body.offsetHeight - 200) {
      setIsBottom(true);
    } else {
      setIsBottom(false);
    }
  };

  const scrollToContent = () => {
    const content = document.getElementById("content");
    if (content) {
      const contentOffsetTop = content.offsetTop;
      window.scrollTo({
        top: contentOffsetTop - 100,
        behavior: "smooth",
      });
    }
    setScrollDownClicked(true);
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    setScrollDownClicked(false);
  };

  useEffect(() => {
    window.addEventListener("scroll", checkIfBottom);
    return () => {
      window.removeEventListener("scroll", checkIfBottom);
    };
  }, []);

  const tickerItems = [
    { name: "ZERO", price: "$0.124", change: "+12.5%", icon: <TiFlash /> },
    { name: "BTC", price: "$98,402", change: "+2.1%", icon: <FaBitcoin /> },
    { name: "ETH", price: "$3,412", change: "+0.8%", icon: <FaEthereum /> },
    { name: "SOL", price: "$245.2", change: "+5.4%", icon: <TiArrowUp /> },
    { name: "ART-INDEX", price: "2.4 BTC", change: "-0.2%", icon: <FaPalette /> },
    { name: "MOO-LAH", price: "$1.02", change: "+0.1%", icon: <FaUniversity /> },
  ];

  const assetMetrics = [
    {
      title: "Ecosystem Liquidity",
      value: "$1.24B",
      desc: "Aggregated liquidity across the 12-brand alliance protocols.",
      icon: <FaFileInvoiceDollar />,
      trend: "+14.2%",
      up: true
    },
    {
      title: "Consensus Health",
      value: "99.99%",
      desc: "Real-time state validation across all Zero-Block L1 validator nodes.",
      icon: <FaShieldAlt />,
      trend: "+0.01%",
      up: true
    },
    {
      title: "Digital Heritage",
      value: "420 ETH",
      desc: "Institutional trading volume within the Zero-Art / ZeroArtNFT sector.",
      icon: <FaPalette />,
      trend: "+8.4%",
      up: true
    },
    {
      title: "Moo-Lah Intelligence",
      value: "84/100",
      desc: "Market sentiment heuristic and wealth growth probability index.",
      icon: <TiChartLineOutline />,
      trend: "-2.1%",
      up: false
    }
  ];

  const ledgerEvents = [
    { id: "#12051", event: "Moo-Lah Syndicate validated block", time: "2s ago", type: "Consensus" },
    { id: "#12050", event: "Zero-Art NFT Collection deployed", time: "14s ago", type: "Art" },
    { id: "#12049", event: "Alliance Liquidity swap: $250k ZERO", time: "32s ago", type: "Finance" },
    { id: "#12048", event: "Modish Core SDK v4.0 sync complete", time: "1m ago", type: "Tech" },
    { id: "#12047", event: "New Validator Node: London HQ", time: "2m ago", type: "Security" },
    { id: "#12046", event: "Institutional Report generated", time: "5m ago", type: "Intel" },
    { id: "#12045", event: "ReguSync protocol check: PASS", time: "8m ago", type: "Legal" }
  ];

  return (
    <div className="w-screen min-h-screen bg-black text-blue-50 font-sans">
      {/* Hero Section */}
      <div className="relative w-full h-screen flex flex-col items-center justify-center p-5">
        <div className="relative w-full max-w-6xl h-64 md:h-[500px] overflow-hidden rounded-[4rem] border border-white/10 z-10 bg-black shadow-[0_0_80px_rgba(34,211,238,0.15)]">
          <video
            src="/videos/market-data-hero.mp4"
            autoPlay
            loop
            muted
            playsInline
            className="absolute left-0 top-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black" />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <SparklesCore
              id="marketHeroSparkles"
              background="transparent"
              minSize={0.6}
              maxSize={1.4}
              particleDensity={50}
              particleColor="#22d3ee"
              className="absolute inset-0"
            />
            <AnimatedTitle
              title="Sovereign Intel"
              containerClass="text-center text-blue-50 font-zentry relative z-10"
            />
            <motion.p 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-cyan-400/80 font-bold tracking-[0.4em] uppercase text-[10px] mt-6 z-10"
            >
              Institutional Intelligence for the 12-Brand Alliance
            </motion.p>
          </div>
        </div>

        {/* Live Ticker Bar */}
        <div className="absolute bottom-20 w-full bg-white/5 border-y border-white/10 backdrop-blur-md py-4 overflow-hidden z-20">
           <div ref={tickerRef} className="flex whitespace-nowrap gap-16 items-center">
              {[...tickerItems, ...tickerItems].map((item, idx) => (
                <div key={idx} className="flex items-center gap-4 group cursor-pointer">
                   <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center text-blue-400">
                      {item.icon}
                   </div>
                   <div className="flex flex-col">
                      <span className="text-[10px] font-bold text-gray-500 tracking-widest">{item.name}</span>
                      <div className="flex items-center gap-2">
                         <span className="text-sm font-bold text-white tracking-widest">{item.price}</span>
                         <span className={`text-[10px] font-bold ${item.change.startsWith('+') ? 'text-emerald-500' : 'text-rose-500'}`}>{item.change}</span>
                      </div>
                   </div>
                </div>
              ))}
           </div>
        </div>
      </div>

      {/* Scroll Down Button */}
      {!scrollDownClicked && (
        <ScrollDownButton
          ref={scrollDownRef}
          onClick={scrollToContent}
        />
      )}

      {/* Content Section */}
      <div id="content" className="px-6 md:px-10 py-32 mx-auto max-w-7xl">
        
        {/* Institutional Metrics Bento */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-48">
          {assetMetrics.map((metric, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="group p-10 rounded-[3rem] bg-white/5 border border-white/10 hover:border-blue-500/30 transition-all hover:bg-white/10 h-full flex flex-col relative"
              ref={(el) => (sectionRefs.current[index] = el)}
            >
              <div className="absolute top-8 right-8 text-blue-500/20 group-hover:scale-110 transition-transform">
                {metric.icon}
              </div>
              <div className="text-[9px] font-bold text-gray-500 uppercase tracking-[0.3em] mb-4">{metric.title}</div>
              <h3 className="text-4xl font-bold mb-4 text-white tracking-widest">{metric.value}</h3>
              <div className={`text-[10px] font-bold ${metric.up ? 'text-emerald-500' : 'text-rose-500'} flex items-center gap-1 mb-8`}>
                 {metric.up ? <FaArrowUp /> : <FaArrowDown />} {metric.trend} <span className="text-gray-600 ml-1">vs L30D</span>
              </div>
              <p className="text-gray-300 text-xs leading-relaxed flex-grow italic pr-4">{metric.desc}</p>
            </motion.div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-16 mb-48">
          {/* High-Fidelity Terminal Charting Suite */}
          <div className="w-full lg:w-3/5 p-1 rounded-[4rem] bg-gradient-to-br from-blue-500/20 to-transparent border border-white/10 overflow-hidden shadow-2xl">
             <div className="bg-black/80 backdrop-blur-3xl p-12 md:p-16 relative overflow-hidden">
                <div className="flex justify-between items-center mb-12">
                   <div className="flex items-center gap-3 text-blue-400 font-bold uppercase tracking-[0.4em] text-[10px]">
                      <TiChartArea className="text-2xl" />
                      Executive Terminal
                   </div>
                   <div className="flex gap-4">
                      {["Volume", "Yield", "Growth"].map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveChart(tab)}
                          className={`px-6 py-2 rounded-xl text-[9px] font-bold uppercase tracking-widest transition-all border ${
                            activeChart === tab 
                              ? "bg-blue-600 border-blue-500 text-black shadow-lg shadow-blue-500/20" 
                              : "bg-white/5 border-white/10 text-gray-500 hover:bg-white/10"
                          }`}
                        >
                          {tab}
                        </button>
                      ))}
                   </div>
                </div>

                <div className="relative h-80 w-full mb-12 bg-white/[0.02] rounded-3xl border border-white/5 p-8 flex items-end justify-between gap-2 group">
                   {/* Simulated Chart Bars */}
                   {[...Array(15)].map((_, i) => (
                     <motion.div 
                       key={i}
                       initial={{ height: 0 }}
                       whileInView={{ height: `${20 + Math.random() * 80}%` }}
                       transition={{ delay: i * 0.05, duration: 1 }}
                       className={`w-full rounded-t-lg ${activeChart === 'Yield' ? 'bg-indigo-500' : activeChart === 'Growth' ? 'bg-cyan-500' : 'bg-blue-600'} opacity-70 group-hover:opacity-100 transition-opacity`}
                     />
                   ))}
                   <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <div className="px-6 py-3 bg-blue-600 text-black font-bold rounded-xl text-xs uppercase tracking-widest shadow-2xl">
                         {activeChart} Analysis: High Institutional Demand
                      </div>
                   </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                   <div className="p-8 rounded-[2rem] bg-white/5 border border-white/5">
                      <div className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mb-4">Historical Alpha</div>
                      <div className="text-2xl font-bold text-white">+84.2%</div>
                      <div className="text-[9px] text-emerald-500 font-bold mt-1">Institutional Consensus</div>
                   </div>
                   <div className="p-8 rounded-[2rem] bg-white/5 border border-white/5">
                      <div className="text-[10px] font-bold text-gray-600 uppercase tracking-widest mb-4">Market Cap Weight</div>
                      <div className="text-2xl font-bold text-white">Direct Exposure</div>
                      <div className="text-[9px] text-blue-500 font-bold mt-1">12-Brand Strategic Depth</div>
                   </div>
                </div>
             </div>
          </div>

          {/* Quantum Ledger Interface */}
          <div className="w-full lg:w-2/5 flex flex-col">
             <div className="p-10 rounded-[3.5rem] bg-white/5 border border-white/10 flex flex-col h-full shadow-xl">
                <div className="flex items-center justify-between mb-10">
                   <div className="flex items-center gap-3 text-blue-400 font-bold uppercase tracking-[0.4em] text-[10px]">
                      <TiCompass className="text-2xl" />
                      Quantum Ledger
                   </div>
                   <div className="text-[10px] font-bold text-emerald-500 animate-pulse tracking-widest">LIVE STREAM</div>
                </div>

                <div className="space-y-6 overflow-hidden max-h-[500px] mask-gradient-b">
                   <AnimatePresence>
                      {ledgerEvents.map((event, index) => (
                        <motion.div
                          key={event.id}
                          initial={{ opacity: 0, x: 20 }}
                          animate={{ opacity: 1 - (index * 0.15), x: 0 }}
                          className="p-6 rounded-3xl bg-white/[0.03] border border-white/5 flex items-center justify-between group hover:bg-white/[0.08] transition-colors"
                        >
                           <div className="flex items-center gap-4">
                              <div className="text-[10px] font-mono text-blue-500 font-bold">{event.id}</div>
                              <div className="flex flex-col">
                                 <span className="text-xs font-bold text-white">{event.event}</span>
                                 <span className="text-[9px] text-gray-600 uppercase font-bold tracking-widest mt-1">{event.type}</span>
                              </div>
                           </div>
                           <div className="text-[9px] font-bold text-gray-600 uppercase">{event.time}</div>
                        </motion.div>
                      ))}
                   </AnimatePresence>
                </div>

                <div className="mt-auto pt-10 text-center">
                   <GlassButton
                     onClick={() => {}}
                     variant="blue"
                     size="sm"
                     className="px-6 py-2"
                   >
                     <div className="flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.3em]">
                       Access Detailed Ledger <TiArrowRight className="text-xl" />
                     </div>
                   </GlassButton>
                </div>
             </div>
          </div>
        </div>

        {/* Executive CTA */}
        <motion.div
           initial={{ opacity: 0, scale: 0.95 }}
           whileInView={{ opacity: 1, scale: 1 }}
           viewport={{ once: true }}
           className="relative text-center py-48"
        >
           <div className="absolute inset-0 bg-blue-500/5 rounded-[100%] blur-[150px]" />
           <div className="relative z-10 max-w-4xl mx-auto px-6">
              <div className="w-24 h-24 rounded-[2.5rem] bg-blue-500/10 border border-blue-500/20 flex items-center justify-center text-5xl text-blue-400 mx-auto mb-16 shadow-[0_0_50px_rgba(59,130,246,0.2)]">
                <TiNews />
              </div>
              <h2 className="text-5xl md:text-7xl font-bold mb-12 leading-tight">
                 Access <span className="font-zentry italic text-blue-400 text-8xl uppercase tracking-tighter block mt-4">Intelligence</span> Archive
              </h2>
              <p className="text-gray-300 text-xl leading-relaxed mb-20 max-w-2xl mx-auto italic">
                Secure the latest institutional-grade market data and liquidity reports for the Zero-Ecosystem.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center">
                 <GlassButton
                   variant="blue"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   Download Full Report
                 </GlassButton>
                 
                 <GlassButton
                   variant="gray"
                   size="lg"
                   className="px-16 py-8 text-xl"
                   onClick={() => {}}
                 >
                   <div className="flex items-center gap-4">
                     Syndicate Dashboard <TiArrowRight className="text-2xl text-blue-400" />
                   </div>
                 </GlassButton>
              </div>

              <div className="mt-32 flex flex-wrap justify-center gap-16 opacity-30 grayscale hover:grayscale-0 transition-all duration-700">
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaShieldAlt /> INSTITUTIONAL GRADE</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaCubes size={18} /> L1 VALIDATION</div>
                 <div className="flex items-center gap-2 font-bold tracking-[0.3em] text-[9px]"><FaGlobe size={18} /> GLOBAL SYNERGY</div>
              </div>
           </div>
        </motion.div>

      </div>

      {/* Back to Top */}
      {isBottom && (
        <div
          ref={scrollTopRef}
          className="fixed bottom-10 left-5 flex items-center gap-3 px-5 py-3 bg-black/40 backdrop-blur-sm border border-blue-500/30 rounded-full shadow-lg shadow-blue-500/10 cursor-pointer hover:bg-black/60 hover:border-blue-400/50 transition-all duration-500 z-50 group"
          onClick={scrollToTop}
        >
          <TiArrowUp className="text-blue-400 group-hover:-translate-y-1 transition-transform duration-500" size={24} />
          <span className="relative z-20 text-sm uppercase text-white/80 font-medium tracking-wider">BACK TO TOP</span>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default MarketData;
